'use client'

import {
  CaretLeftIcon,
  CaretRightIcon,
  StarHalfIcon,
  StarIcon,
} from '@phosphor-icons/react'
import clsx from 'clsx'
import { EmblaCarouselType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import { useCallback, useEffect, useState } from 'react'
import { FahrlehrerCh } from '@/src/@types/blinkadmin'
import { PageInfo } from '@/src/@types/models'
import { FlchReviewsBlock } from '@/src/@types/statamic'
import { flchUrl, get } from '@/src/BlinkAdminApiClient'
import Button from '@/src/components/core/Button'
import { formatGerman } from '@/src/components/core/lib/date'
import ShowMore from '@/src/components/core/ShowMore'
import Container from '@/src/components/layout/Container'

const CATEGORIES: {
  key: keyof FahrlehrerCh.TeacherProfileReviewRatings
  label: string
}[] = [
  { key: 'explanations', label: 'Erklärungen' },
  { key: 'progress', label: 'Fortschritt' },
  { key: 'learningGoals', label: 'Lernziele' },
  { key: 'attention', label: 'Aufmerksamkeit' },
  { key: 'patience', label: 'Geduld' },
  { key: 'scheduling', label: 'Flexibilität' },
  { key: 'cleanliness', label: 'Sauberkeit' },
  { key: 'punctuality', label: 'Pünktlichkeit' },
]

function avg(values: (number | null)[]): number {
  const valid = values.filter((v): v is number => v !== null)
  return valid.length > 0 ? valid.reduce((s, v) => s + v, 0) / valid.length : 5
}

function reviewAvg(ratings: FahrlehrerCh.TeacherProfileReviewRatings): number {
  return avg([
    ratings.progress,
    ratings.explanations,
    ratings.learningGoals,
    ratings.attention,
    ratings.patience,
    ratings.scheduling,
    ratings.cleanliness,
    ratings.punctuality,
  ])
}

function topCategories(review: FahrlehrerCh.TeacherProfileReview) {
  return CATEGORIES.flatMap(({ key, label }) => {
    const score = review.ratings[key]
    return score !== null ? [{ key, label, score }] : []
  })
    .sort((a, b) => b.score - a.score)
    .slice(0, 4)
}

function fmt(n: number): string {
  return n.toFixed(1)
}

export async function getStaticProps(props: FlchReviewsBlock) {
  const reviews = await get<FahrlehrerCh.TeacherProfileReview[]>(
    flchUrl('reviews')
  )

  return { ...props, reviews: reviews ?? [] }
}

export default function Component({
  title,
  text,
  reviews,
  pageInfo,
}: FlchReviewsBlock & {
  reviews: FahrlehrerCh.TeacherProfileReview[]
  pageInfo: PageInfo
}) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    breakpoints: {
      '(min-width: 768px)': { skipSnaps: true },
    },
  })
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  const onSelect = useCallback((api: EmblaCarouselType) => {
    setCanScrollPrev(api.canScrollPrev())
    setCanScrollNext(api.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi) return
    onSelect(emblaApi)
    emblaApi.on('select', onSelect).on('reInit', onSelect)
  }, [emblaApi, onSelect])

  if (!reviews?.length) return null

  const sorted = [...reviews].sort((a, b) => {
    if (!!a.text !== !!b.text) {
      return (b.text ? 1 : 0) - (a.text ? 1 : 0)
    }
    return new Date(b.date).getTime() - new Date(a.date).getTime()
  })

  return (
    <section
      className={clsx('bg-linear-to-b from-gray-50 to-gray-100 py-24', {
        'mt-20 md:mt-32': pageInfo.newSpacing,
      })}
    >
      <Container>
        <div className="flex max-w-3xl flex-col gap-4 md:gap-6">
          <h2 className="text-4xl font-black text-balance md:text-20xl">
            {title}
          </h2>
          <p className="text-gray-800 md:text-lg">{text}</p>
        </div>
      </Container>

      <div ref={emblaRef} className="mt-8 overflow-hidden md:mt-10">
        <div className="flex items-start select-none">
          {sorted.map((review, i) => (
            <div
              key={i}
              className="ml-5 min-w-0 flex-[0_0_calc(100%-40px)] last:mr-5 min-[1342px]:first:ml-[calc((100%-1280px)/2)] min-[1342px]:last:mr-[calc((100%-1280px)/2)]! md:ml-6 md:flex-[0_0_410px] md:last:mr-6"
            >
              <ReviewCard review={review} />
            </div>
          ))}
        </div>
      </div>

      <Container>
        <div className="mt-6 flex justify-end gap-3">
          <Button
            icon={<CaretLeftIcon weight="bold" />}
            size="icon-small"
            version="gray-outlined"
            onClick={() => emblaApi?.scrollPrev()}
            disabled={!canScrollPrev}
            aria-label="Vorherige Bewertung"
          />
          <Button
            icon={<CaretRightIcon weight="bold" />}
            size="icon-small"
            version="gray-outlined"
            onClick={() => emblaApi?.scrollNext()}
            disabled={!canScrollNext}
            aria-label="Nächste Bewertung"
          />
        </div>
      </Container>
    </section>
  )
}

function ReviewCard({ review }: { review: FahrlehrerCh.TeacherProfileReview }) {
  const [expanded, setExpanded] = useState(!review.text)
  const rating = reviewAvg(review.ratings)
  const categories = topCategories(review)

  return (
    <div className="flex h-full flex-col rounded-xl border border-gray-200 bg-white px-6 py-6 shadow-xs">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-bold">{review.firstName ?? 'anonym'}</p>
          <p className="text-sm text-gray-600">
            {formatGerman(review.date, 'dd. MMMM yyyy')}
          </p>
        </div>
        <Stars rating={rating} />
      </div>

      <ShowMore>
        {({ clampRef, isClamped }) => {
          const canExpand = isClamped || categories.length > 0

          return (
            <>
              {review.text && (
                <p
                  ref={clampRef}
                  className={clsx('mt-4 text-gray-800', {
                    'line-clamp-4': !expanded,
                  })}
                >
                  {review.text}
                </p>
              )}

              {expanded && categories.length > 0 && (
                <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-4">
                  {categories.map(({ key, label, score }) => (
                    <div key={key} className="flex flex-col gap-1">
                      <span className="text-sm text-gray-800">{label}</span>
                      <div className="flex items-center gap-1">
                        <DotRating score={score} />
                        <span className="text-sm text-gray-800 tabular-nums">
                          {fmt(score)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!expanded && canExpand && (
                <button
                  className="mt-4 self-start text-sm font-bold text-blue-700"
                  onClick={() => setExpanded(true)}
                >
                  Mehr anzeigen
                </button>
              )}
            </>
          )
        }}
      </ShowMore>
    </div>
  )
}

function Stars({ rating }: { rating: number }) {
  return (
    <div className="flex shrink-0 gap-0.5" aria-label={`${fmt(rating)} von 5`}>
      {Array.from({ length: 5 }).map((_, i) => {
        const diff = rating - i
        if (diff >= 0.75) {
          return (
            <StarIcon
              key={i}
              weight="fill"
              size={20}
              className="text-yellow-600"
            />
          )
        }
        if (diff >= 0.25) {
          return (
            <StarHalfIcon
              key={i}
              weight="fill"
              size={20}
              className="text-yellow-600"
            />
          )
        }
        return (
          <StarIcon
            key={i}
            weight="regular"
            size={20}
            className="text-gray-300"
          />
        )
      })}
    </div>
  )
}

function DotRating({ score }: { score: number }) {
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: 5 }).map((_, i) => {
        const fill = Math.round(Math.min(Math.max(score - i, 0), 1) * 2) / 2
        return (
          <span
            key={i}
            className="relative block size-3 overflow-hidden rounded-full bg-gray-300"
          >
            {fill > 0 && (
              <span
                className="absolute inset-y-0 left-0 bg-gray-800"
                style={{ width: `${fill * 100}%` }}
              />
            )}
          </span>
        )
      })}
    </div>
  )
}
